/**
 * What an AP row's due date was worked out from (D109.7).
 *
 * The due date is whatever the server's `deriveDueDate` ladder produced (printed date, the
 * invoice's own terms, then the supplier's on-file credit days) and it records which rung as
 * `dueDateSource`. The screen used to show the SUPPLIER's on-file terms ("Net 21") under the due
 * date, which read as the basis even when the date came from the invoice's own "30 days". This
 * states the days actually between the two dates, and where they came from.
 *
 * Client-only display helper: it computes nothing the server stores.
 */

import { toInvoiceDate } from './paymentTerms';

const DAY_MS = 86_400_000;

/** Whole calendar days from the invoice date to the due date, or null when either is missing. */
export function daysUsedForDueDate(invoiceDate?: string | null, dueDate?: string | null): number | null {
  const from = toInvoiceDate(invoiceDate);
  const to = toInvoiceDate(dueDate);
  if (from === null || to === null) return null;
  return Math.round((to.getTime() - from.getTime()) / DAY_MS);
}

/** Where the days came from, by every `dueDateSource` the server can store. */
function sourcePhrase(source?: string | null): string {
  switch (source) {
    case 'printed':
      return 'printed on invoice';
    case 'terms':
      return 'from invoice terms';
    case 'vendor_terms':
      return 'from supplier terms';
    case 'manual':
      return 'entered by hand';
    default:
      return '';
  }
}

/**
 * "30 days · from invoice terms". Empty (no caption) when the source is unknown or missing, or
 * either date is missing: never a guess.
 */
export function dueDateBasisCaption(row: {
  invoiceDate?: string | null;
  dueDate?: string | null;
  dueDateSource?: string | null;
}): string {
  const source = sourcePhrase(row.dueDateSource);
  const days = daysUsedForDueDate(row.invoiceDate, row.dueDate);
  if (!source || days === null) return '';
  return `${days} ${days === 1 ? 'day' : 'days'} · ${source}`;
}
